/**
 * Match state construction, round reset, and the deep clone `stepMatch` works on so the previous
 * state is never mutated. Also the small geometric helpers every rule needs from a combatant.
 */
import { cloneArenaRuntime, freshArenaRuntime } from '../arena/features.js';
import { SIM_TICK_RATE } from '../constants.js';
import { aimDirection, forwardXZ, rightXZ } from '../sim/math.js';
import { cloneRng, seedRng } from '../sim/rng.js';
import { capsuleOf, createMechState, IDLE_INPUT } from '../sim/step-mech.js';
export const DEFAULT_MATCH_CONFIG = Object.freeze({
    mode: 'practice',
    roundSeconds: 180,
    countdownSeconds: 10,
    roundEndSeconds: 5,
    roundsToWin: 2,
    bots: [null, null],
});
export function secondsToTicks(seconds) {
    return Math.max(0, Math.round(seconds * SIM_TICK_RATE));
}
export function emptyStats() {
    return {
        damageDealt: 0,
        damageTaken: 0,
        selfDamage: 0,
        shotsFired: 0,
        shotsHit: 0,
        kills: 0,
        deaths: 0,
        roundsWon: 0,
        timeBonusTicks: 0,
        firstBlood: false,
        abilitiesUsed: 0,
        ultimatesUsed: 0,
    };
}
export function createBotState(level, position, yaw) {
    return {
        level,
        mode: 'approach',
        decisionTicks: 0,
        perceived: null,
        seen: false,
        seenTicks: 0,
        strafeDir: 1,
        aimYaw: yaw,
        aimPitch: 0,
        noiseYaw: 0,
        noisePitch: 0,
        coverTarget: null,
        stuckTicks: 0,
        lastPosition: { ...position },
        jumpTicks: 0,
        holdFireTicks: 0,
    };
}
function freshAbility() {
    return {
        cooldown: 0,
        active: false,
        step: 0,
        ticks: 0,
        toggled: false,
        held: false,
        target: null,
    };
}
function freshWeapons(def, magazines) {
    return def.weapons.map((w) => ({
        id: w.id,
        cooldown: 0,
        ammo: magazines.get(w.id) ?? null,
        reloadTicks: 0,
        spin: 0,
        bloom: 0,
        burstLeft: 0,
        beamTicks: 0,
        beamCoolTicks: 0,
        held: false,
    }));
}
export function createCombatant(slot, def, spawn, bot, magazines, modules = []) {
    return {
        slot,
        mechId: def.id,
        modules: [...modules],
        body: createMechState(def, spawn.position, spawn.yaw),
        hp: def.hp,
        shield: def.shield,
        shieldDelayTicks: 0,
        heat: 0,
        overheatTicks: 0,
        venting: false,
        legDamage: 0,
        armDamage: 0,
        weapons: freshWeapons(def, magazines),
        melee: { cooldown: 0, phase: 'idle', ticks: 0, hit: false },
        abilities: [freshAbility(), freshAbility()],
        ultCharge: 0,
        effects: [],
        bot: bot ? createBotState(bot, spawn.position, spawn.yaw) : null,
        lastInput: { ...IDLE_INPUT, aimYaw: spawn.yaw },
        alive: true,
        platform: null,
    };
}
/** Spawn for a slot, honouring the per-round swap. */
export function spawnFor(state, slot) {
    const index = state.flow.swapped ? (slot === 0 ? 1 : 0) : slot;
    return state.spawns[index];
}
/** Puts both combatants back to full strength at their spawns and clears round entities. */
export function resetRoundState(state, ctx, magazines) {
    for (const slot of [0, 1]) {
        const prev = state.mechs[slot];
        const fresh = createCombatant(slot, ctx.defs[slot], spawnFor(state, slot), prev.bot?.level ?? null, magazines, prev.modules);
        state.mechs[slot] = fresh;
    }
    state.projectiles = [];
    state.deployables = [];
    // Every pickup is back and every pad is ready when a round starts.
    state.features = freshArenaRuntime(ctx.arena.features);
}
export function createMatchState(setup, ctx, magazines) {
    const config = { ...DEFAULT_MATCH_CONFIG, ...setup.config };
    const spawns = [
        { position: { ...setup.spawns[0].position }, yaw: setup.spawns[0].yaw },
        { position: { ...setup.spawns[1].position }, yaw: setup.spawns[1].yaw },
    ];
    const state = {
        version: 1,
        tick: 0,
        seed: setup.seed,
        rng: seedRng(setup.seed),
        arenaId: setup.arenaId,
        config,
        spawns,
        flow: {
            phase: config.countdownSeconds > 0 ? 'countdown' : 'live',
            round: 1,
            ticksLeft: config.countdownSeconds > 0
                ? secondsToTicks(config.countdownSeconds)
                : secondsToTicks(config.roundSeconds),
            wins: [0, 0],
            roundWinner: null,
            swapped: false,
            liveTick: 0,
        },
        mechs: [
            createCombatant(0, ctx.defs[0], spawns[0], config.bots[0], magazines, setup.loadouts?.[0]?.modules ?? []),
            createCombatant(1, ctx.defs[1], spawns[1], config.bots[1], magazines, setup.loadouts?.[1]?.modules ?? []),
        ],
        projectiles: [],
        deployables: [],
        nextId: 1,
        stats: [emptyStats(), emptyStats()],
        firstBlood: null,
        rounds: [],
        events: [{ t: 'roundStart', round: 1 }],
        result: null,
        features: freshArenaRuntime(ctx.arena.features),
    };
    if (state.flow.phase === 'live')
        state.events.push({ t: 'live', round: 1 });
    return state;
}
// --- deep clone (hand-written: about 2 KB of plain data per tick) ---
function cloneVec(v) {
    return { x: v.x, y: v.y, z: v.z };
}
function cloneEffect(e) {
    const p = e.params;
    return {
        id: e.id,
        kind: e.kind,
        source: e.source,
        ticksLeft: e.ticksLeft,
        params: {
            ...p,
            ...(p.flags ? { flags: [...p.flags] } : {}),
            ...(p.dash ? { dash: { ...p.dash } } : {}),
            ...(p.dot ? { dot: { ...p.dot } } : {}),
            ...(p.hot ? { hot: { ...p.hot } } : {}),
            ...(p.parry ? { parry: { ...p.parry } } : {}),
            ...(p.cloak ? { cloak: { ...p.cloak } } : {}),
            ...(p.impulse ? { impulse: cloneVec(p.impulse) } : {}),
            ...(p.burst
                ? { burst: { ...p.burst, target: p.burst.target ? cloneVec(p.burst.target) : null } }
                : {}),
            ...(p.beam ? { beam: { ...p.beam } } : {}),
        },
    };
}
function cloneCombatant(c) {
    return {
        ...c,
        body: { ...c.body, position: cloneVec(c.body.position), velocity: cloneVec(c.body.velocity) },
        weapons: c.weapons.map((w) => ({ ...w })),
        melee: { ...c.melee },
        abilities: [
            { ...c.abilities[0], target: c.abilities[0].target ? cloneVec(c.abilities[0].target) : null },
            { ...c.abilities[1], target: c.abilities[1].target ? cloneVec(c.abilities[1].target) : null },
        ],
        effects: c.effects.map(cloneEffect),
        bot: c.bot
            ? {
                ...c.bot,
                perceived: c.bot.perceived ? cloneVec(c.bot.perceived) : null,
                coverTarget: c.bot.coverTarget ? cloneVec(c.bot.coverTarget) : null,
                lastPosition: cloneVec(c.bot.lastPosition),
            }
            : null,
        lastInput: { ...c.lastInput },
    };
}
function cloneProjectile(p) {
    return { ...p, position: cloneVec(p.position), velocity: cloneVec(p.velocity) };
}
function cloneDeployable(d) {
    const p = d.params;
    return {
        ...d,
        position: cloneVec(d.position),
        params: {
            ...p,
            ...(p.halfExtents ? { halfExtents: cloneVec(p.halfExtents) } : {}),
            ...(p.pulse ? { pulse: { ...p.pulse } } : {}),
            ...(p.detonate ? { detonate: { ...p.detonate } } : {}),
        },
    };
}
/** A deep copy sharing nothing mutable with the source; events are not carried over. */
export function cloneMatchState(s) {
    return {
        version: 1,
        tick: s.tick,
        seed: s.seed,
        rng: cloneRng(s.rng),
        arenaId: s.arenaId,
        config: { ...s.config, bots: [s.config.bots[0], s.config.bots[1]] },
        spawns: [
            { position: cloneVec(s.spawns[0].position), yaw: s.spawns[0].yaw },
            { position: cloneVec(s.spawns[1].position), yaw: s.spawns[1].yaw },
        ],
        flow: { ...s.flow, wins: [s.flow.wins[0], s.flow.wins[1]] },
        mechs: [cloneCombatant(s.mechs[0]), cloneCombatant(s.mechs[1])],
        projectiles: s.projectiles.map(cloneProjectile),
        deployables: s.deployables.map(cloneDeployable),
        nextId: s.nextId,
        stats: [{ ...s.stats[0] }, { ...s.stats[1] }],
        firstBlood: s.firstBlood,
        rounds: s.rounds.map((r) => ({ ...r, hpPct: [r.hpPct[0], r.hpPct[1]] })),
        events: [],
        result: s.result
            ? {
                ...s.result,
                scores: [{ ...s.result.scores[0] }, { ...s.result.scores[1] }],
            }
            : null,
        features: cloneArenaRuntime(s.features),
    };
}
// --- combatant geometry ---
export function combatantCapsule(c, def) {
    const cap = capsuleOf(def);
    return { base: c.body.position, radius: cap.radius, halfHeight: cap.halfHeight };
}
export function poseCapsule(pose, def) {
    const cap = capsuleOf(def);
    return { base: pose.position, radius: cap.radius, halfHeight: cap.halfHeight };
}
/** Torso centre. */
export function centerOf(pose, def) {
    return {
        x: pose.position.x,
        y: pose.position.y + def.capsule.height / 2,
        z: pose.position.z,
    };
}
/** Where shots and rays start: the aim height on the mech axis. */
export function aimOrigin(pose, def, aimHeightFraction) {
    return {
        x: pose.position.x,
        y: pose.position.y + def.capsule.height * aimHeightFraction,
        z: pose.position.z,
    };
}
/** World position of a muzzle offset (right, up, forward) in the mech's aim frame. */
export function muzzlePosition(pose, offset) {
    const f = forwardXZ(pose.aimYaw);
    const r = rightXZ(pose.aimYaw);
    return {
        x: pose.position.x + r.x * offset.right + f.x * offset.forward,
        y: pose.position.y + offset.up,
        z: pose.position.z + r.z * offset.right + f.z * offset.forward,
    };
}
export function aimDir(pose) {
    return aimDirection(pose.aimYaw, pose.aimPitch);
}
export function releasedInput(prev) {
    return {
        ...IDLE_INPUT,
        seq: prev.seq,
        aimYaw: prev.aimYaw,
        aimPitch: prev.aimPitch,
    };
}
//# sourceMappingURL=state.js.map